import type {
  BarcodeElementTemplate,
  BarcodeTemplateInput,
  BarcodeWidthPolicy,
  BoxElementTemplate,
  FontTemplate,
  LabelElementTemplate,
  TextElementTemplate,
  TextFitMode,
  TextPresencePolicy,
  TextTransform,
} from "../types/template";
import { assertNonNegativeSpacing, normalizeSpacing } from "../utils/spacing";
import { assertFinite, assertPositive, assertPositiveInteger } from "./helpers";

function validateTextFont(
  font: Partial<FontTemplate> | undefined,
  path: string,
  issues: string[],
): void {
  if (font === undefined) {
    return;
  }

  assertPositive(font.heightMm, `${path}.heightMm`, issues, true);
  assertPositive(font.widthMm, `${path}.widthMm`, issues, true);
  assertPositive(font.lineSpacingMm, `${path}.lineSpacingMm`, issues);
  assertPositive(font.maxLines, `${path}.maxLines`, issues, true);
  assertPositive(font.minHeightMm, `${path}.minHeightMm`, issues, true);
}

function validateTextTransform(
  transform: TextTransform | undefined,
  path: string,
  issues: string[],
): void {
  if (transform === undefined) {
    return;
  }

  if (transform !== "none" && transform !== "upper" && transform !== "lower") {
    issues.push(`${path} must be 'none', 'upper', or 'lower'.`);
  }
}

function validateTextFitMode(
  fitMode: TextFitMode | undefined,
  path: string,
  issues: string[],
): void {
  if (fitMode === undefined) {
    return;
  }

  if (fitMode !== "word-wrap" && fitMode !== "char-fill") {
    issues.push(`${path} must be 'word-wrap' or 'char-fill'.`);
  }
}

function validateTextPresence(
  presence: TextPresencePolicy | undefined,
  path: string,
  issues: string[],
): void {
  if (presence === undefined) {
    return;
  }

  if (presence !== "required" && presence !== "optional-hide") {
    issues.push(`${path} must be 'required' or 'optional-hide'.`);
  }
}

function validateBarcodeWidthPolicy(
  widthPolicy: BarcodeWidthPolicy | undefined,
  path: string,
  issues: string[],
): void {
  if (widthPolicy === undefined) {
    return;
  }

  if (widthPolicy.mode === "fixed-module") {
    assertPositive(
      widthPolicy.moduleWidthMm,
      `${path}.moduleWidthMm`,
      issues,
      true,
    );
    return;
  }

  assertPositiveInteger(
    widthPolicy.minModuleDots,
    `${path}.minModuleDots`,
    issues,
  );
  assertPositiveInteger(
    widthPolicy.maxModuleDots,
    `${path}.maxModuleDots`,
    issues,
  );
  if (
    widthPolicy.maxModuleDots !== undefined &&
    widthPolicy.minModuleDots > widthPolicy.maxModuleDots
  ) {
    issues.push(
      `${path}.maxModuleDots must be greater than or equal to minModuleDots.`,
    );
  }
}

function validateBarcodeHAlign(
  hAlign: BarcodeElementTemplate["hAlign"] | BarcodeTemplateInput["hAlign"],
  path: string,
  issues: string[],
): void {
  if (hAlign === undefined) {
    return;
  }

  if (hAlign !== "left" && hAlign !== "center" && hAlign !== "right") {
    issues.push(`${path} must be 'left', 'center', or 'right'.`);
  }
}

function validateTextElement(
  element: TextElementTemplate,
  path: string,
  issues: string[],
): void {
  if (!element.source || typeof element.source !== "string") {
    issues.push(`${path}.source must be a non-empty string.`);
  }

  validateTextFont(element.font, `${path}.font`, issues);
  validateTextFitMode(element.fitMode, `${path}.fitMode`, issues);
  validateTextTransform(element.transform, `${path}.transform`, issues);
  validateTextPresence(element.presence, `${path}.presence`, issues);

  if (
    element.valueTemplate !== undefined &&
    element.valueTemplate.trim().length === 0
  ) {
    issues.push(`${path}.valueTemplate must be non-empty when provided.`);
  }
}

function validateBoxElement(
  element: BoxElementTemplate,
  path: string,
  issues: string[],
): void {
  assertPositive(
    element.borderThicknessMm,
    `${path}.borderThicknessMm`,
    issues,
    true,
  );

  if (element.fill !== undefined && typeof element.fill !== "boolean") {
    issues.push(`${path}.fill must be a boolean.`);
  }

  if (element.cornerRounding !== undefined) {
    assertPositive(element.cornerRounding, `${path}.cornerRounding`, issues);
    if (
      Number.isFinite(element.cornerRounding) &&
      element.cornerRounding !== undefined &&
      (element.cornerRounding < 0 || element.cornerRounding > 8)
    ) {
      issues.push(`${path}.cornerRounding must be between 0 and 8.`);
    }
  }
}

function validateBarcodeElement(
  element: BarcodeElementTemplate,
  path: string,
  issues: string[],
): void {
  if (!element.source || typeof element.source !== "string") {
    issues.push(`${path}.source must be a non-empty string.`);
  }

  if (
    element.symbology !== undefined &&
    element.symbology !== "code128" &&
    element.symbology !== "qrcode"
  ) {
    issues.push(`${path}.symbology must be 'code128' or 'qrcode'.`);
  }

  validateBarcodeHAlign(element.hAlign, `${path}.hAlign`, issues);

  if (element.symbology === "qrcode") {
    if (element.magnification !== undefined) {
      if (
        !Number.isInteger(element.magnification) ||
        element.magnification < 1 ||
        element.magnification > 10
      ) {
        issues.push(
          `${path}.magnification must be an integer between 1 and 10.`,
        );
      }
    }
    if (element.errorCorrection !== undefined) {
      if (
        element.errorCorrection !== "L" &&
        element.errorCorrection !== "M" &&
        element.errorCorrection !== "Q" &&
        element.errorCorrection !== "H"
      ) {
        issues.push(`${path}.errorCorrection must be 'L', 'M', 'Q', or 'H'.`);
      }
    }
  } else {
    validateBarcodeWidthPolicy(
      element.widthPolicy,
      `${path}.widthPolicy`,
      issues,
    );
    assertPositive(
      element.wideToNarrowRatio,
      `${path}.wideToNarrowRatio`,
      issues,
      true,
    );

    if (
      element.widthPolicy?.mode === "fit-inside" &&
      element.widthMm === undefined
    ) {
      issues.push(
        `${path}.widthMm is required when widthPolicy.mode is 'fit-inside'.`,
      );
    }

    if (
      element.optimizeNumeric !== undefined &&
      typeof element.optimizeNumeric !== "boolean"
    ) {
      issues.push(`${path}.optimizeNumeric must be a boolean.`);
    }
  }
}

export function validateBarcodeDefaults(
  barcodeDefaults: BarcodeTemplateInput | undefined,
  path: string,
  issues: string[],
): void {
  if (barcodeDefaults === undefined) {
    return;
  }

  assertPositive(barcodeDefaults.heightMm, `${path}.heightMm`, issues, true);
  assertPositive(
    barcodeDefaults.wideToNarrowRatio,
    `${path}.wideToNarrowRatio`,
    issues,
    true,
  );
  validateBarcodeWidthPolicy(
    barcodeDefaults.widthPolicy,
    `${path}.widthPolicy`,
    issues,
  );
  validateBarcodeHAlign(barcodeDefaults.hAlign, `${path}.hAlign`, issues);

  if (
    barcodeDefaults.optimizeNumeric !== undefined &&
    typeof barcodeDefaults.optimizeNumeric !== "boolean"
  ) {
    issues.push(`${path}.optimizeNumeric must be a boolean.`);
  }
}

export function validateElement(
  element: LabelElementTemplate,
  path: string,
  usedElementIds: Set<string>,
  referencedVariablePaths: Set<string>,
  requiredSourcePaths: Set<string>,
  issues: string[],
): void {
  if (usedElementIds.has(element.id)) {
    issues.push(
      `${path}.id '${element.id}' must be unique within template elements.`,
    );
  }
  usedElementIds.add(element.id);

  assertPositive(element.widthMm, `${path}.widthMm`, issues, true);
  assertPositive(element.heightMm, `${path}.heightMm`, issues, true);
  assertFinite(element.nudgeXMm, `${path}.nudgeXMm`, issues);
  assertFinite(element.nudgeYMm, `${path}.nudgeYMm`, issues);

  const margin = normalizeSpacing(element.marginMm);
  assertNonNegativeSpacing(margin, `${path}.marginMm`, issues);

  switch (element.type) {
    case "text":
      validateTextElement(element, path, issues);
      referencedVariablePaths.add(element.source);
      if ((element.presence ?? "required") === "required") {
        requiredSourcePaths.add(element.source);
      }
      break;
    case "barcode":
      validateBarcodeElement(element, path, issues);
      referencedVariablePaths.add(element.source);
      requiredSourcePaths.add(element.source);
      break;
    case "line":
      assertPositive(element.thicknessMm, `${path}.thicknessMm`, issues, true);
      break;
    case "box":
      validateBoxElement(element, path, issues);
      break;
    case "spacer":
      break;
    default:
      issues.push(`${path}.type is not supported.`);
  }
}

export function validateFontDefaults(
  fontDefaults: Partial<TextElementTemplate["font"]> | undefined,
  path: string,
  issues: string[],
): void {
  validateTextFont(fontDefaults, path, issues);
}
